import { z } from "zod";
import { cognitoUuid } from "./validation-common";

// ─── Enums ───────────────────────────────────────────────────────────────────

export const InterviewTypeSchema = z.enum(["in_person", "virtual"]);
export type InterviewType = z.infer<typeof InterviewTypeSchema>;

export const InterviewStatusSchema = z.enum([
  "pending_confirmation",
  "confirmed",
  "completed",
  "canceled",
]);
export type InterviewStatus = z.infer<typeof InterviewStatusSchema>;

// ─── Core Entity ─────────────────────────────────────────────────────────────

export const InterviewSchema = z.object({
  interviewId: z.uuid(),
  applicationId: z.uuid(),
  candidateId: cognitoUuid,
  jobPostingId: z.uuid(),
  companyId: z.uuid(),
  candidateName: z.string(),
  role: z.string(),
  type: InterviewTypeSchema,
  status: InterviewStatusSchema,
  duration: z.number().int().positive(),
  location: z.string().optional(),
  availableSlots: z.array(z.string()),
  selectedSlot: z.string().optional(),
  scheduledAt: z.string().optional(),
  feedbackNotes: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Interview = z.infer<typeof InterviewSchema>;

// ─── Response Schemas ─────────────────────────────────────────────────────────

export const InterviewListResponseSchema = z.object({
  upcoming: z.array(InterviewSchema),
  pastPendingFeedback: z.array(InterviewSchema),
});
export type InterviewListResponse = z.infer<typeof InterviewListResponseSchema>;

export const SelectSlotAckSchema = z.object({
  message: z.string(),
  interviewId: z.uuid(),
  selectedSlot: z.string(),
  status: z.literal("confirmed"),
});
export type SelectSlotAck = z.infer<typeof SelectSlotAckSchema>;

export const InterviewUpdateAckSchema = z.object({
  message: z.string(),
  interviewId: z.uuid(),
});
export type InterviewUpdateAck = z.infer<typeof InterviewUpdateAckSchema>;

// ─── Request Schemas ──────────────────────────────────────────────────────────

export const CreateInterviewRequestSchema = z.object({
  applicationId: z.uuid(),
  candidateId: cognitoUuid,
  jobPostingId: z.uuid(),
  candidateName: z.string().min(1),
  role: z.string().min(1),
  type: InterviewTypeSchema,
  duration: z.number().int().positive(),
  availableSlots: z.array(z.string()).min(1),
  location: z.string().optional(),
});
export type CreateInterviewRequest = z.infer<
  typeof CreateInterviewRequestSchema
>;

export const SelectInterviewSlotRequestSchema = z.object({
  selectedSlot: z.string(),
});
export type SelectInterviewSlotRequest = z.infer<
  typeof SelectInterviewSlotRequestSchema
>;

export const UpdateInterviewRequestSchema = z.object({
  availableSlots: z.array(z.string()).optional(),
  location: z.string().optional(),
  type: InterviewTypeSchema.optional(),
  duration: z.number().int().positive().optional(),
  status: z.enum(["canceled", "completed"]).optional(),
  feedbackNotes: z.string().optional(),
});
export type UpdateInterviewRequest = z.infer<
  typeof UpdateInterviewRequestSchema
>;
